import {
  useState,
  useMemo,
  FunctionComponent,
  useEffect,
  useCallback,
} from "react";
import { Column, Connection, Id } from "./types";
import ColumnContainer from "./ColumnContainer";
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useSensors,
  useSensor,
  PointerSensor,
} from "@dnd-kit/core";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import { createPortal } from "react-dom";
import ConnectionCard from "./ConnectionCard";
import styled from "styled-components";
import { IConnection } from "../../../../interfaces/models/connection";
import { styles } from "../../../../styles/themes/style";
import { useConnectionsHandlers } from "../../../../services/actions/connections/useConnectionsHandlers";
import { useAuthStore } from "../../../../stores/auth/useAuthStore";
import Text from "../../../../components/Text";
import { IKanban } from "../../../../interfaces/models/kanban";
import { SidebarTypes } from "../../../../interfaces/store/appStore";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import TableCell from "../../../../components/table/TableCell";
import LoaderCell from "../../../../components/LoaderCell";
import SvgFragment from "../../../../fragments/SvgFragment";
import KanbanInfoPopup from "../../../../components/KanbanInfoPopup";
import { useAppActions } from "../../../../stores/application/useAppActions";
import { ModalActionType } from "../../../../components/modal/AppModal";
import { useAppStore } from "../../../../stores/application/useAppStore";
import { useTagsHandlers } from "../../../../services/actions/tags/useTagsHandlers";

interface IKanbanBoardProps {
  selectedConnections: IConnection[];
}

const getSelectedKanbanStorageKey = (workspaceId: string) =>
  `selectedKanbanId_${workspaceId}`;

const getStoredKanbanId = (workspaceId?: string): string | null => {
  if (!workspaceId) return null;
  return localStorage.getItem(getSelectedKanbanStorageKey(workspaceId));
};

const setStoredKanbanId = (workspaceId: string | undefined, kanbanId: string) => {
  if (!workspaceId) return;
  localStorage.setItem(getSelectedKanbanStorageKey(workspaceId), kanbanId);
};

const KanbanBoard: FunctionComponent<IKanbanBoardProps> = ({
  selectedConnections,
}) => {
  const [columns, setColumns] = useState<Column[]>([]);
  const [activeColumn, setActiveColumn] = useState<Column | null>(null);
  const [kanban, setKanban] = useState<IKanban | undefined>(undefined);
  const [allKanbans, setAllKanbans] = useState<IKanban[]>([]);
  const [activeConnection, setActiveConnection] = useState<Connection | null>(
    null
  );
  const [kanbanLoading, setKanbanLoading] = useState<boolean>(true);
  const [connections, setConnections] = useState<Connection[]>(
    selectedConnections.map((connection) => ({
      id: generateId(),
      columnId: columns && columns.length && columns[0].id,
      content: connection.firstName + " " + connection.lastName,
      connection: connection,
    }))
  );
  const [columnStates, setColumnStates] = useState<{
    [key: string]: { offset: number; hasMore: boolean; loading: boolean };
  }>({});
  const LIMIT = 20;

  const {
    store: { workspace },
  } = useAuthStore();
  const { openSidebar, setDestructiveModal, hideModal } = useAppHandlers();
  const {
    getKanbanWithConnectionsHandler,
    updateConnectionColumnIdInKanbanHandler,
    addColumnToKanbanHandler,
    updateKanbanColumnHandler,
    addConnectionToKanbanHandler,
    updateKanbanHandler,
    removeConnectionFromKanbanHandler,
    reorderKanbanColumnsHandler,
    removeKanbanHandler,
    removeKanbanColumnHandler,
    getKanbansHandler,
    getConnectionByPublicIdentifierHandler,
    fetchConnectionByIdHandler,
  } = useConnectionsHandlers();
  const { setModal } = useAppActions();
  const {
    store: { requestLoading },
  } = useAppStore();
  const { loadAllTagsHandler } = useTagsHandlers();

  const columnsId = useMemo(() => columns.map((col) => col.id), [columns]);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 3 },
    })
  );

  const [dragOperation, setDragOperation] = useState<{
    activeId: Id | null;
    overId: Id | null;
    activeIndex: number | null;
    overIndex: number | null;
    newColumnId: Id | null;
  }>({
    activeId: null,
    overId: null,
    activeIndex: null,
    overIndex: null,
    newColumnId: null,
  });

  const findPValue = (key: string, connection: any) => {
    const item = connection.p?.find((entry: { k: string }) => entry.k === key);
    if (item?.v !== undefined && item?.v !== null && item?.v !== "") return item.v;
    return connection?.[key] ?? "";
  };

  const mergeKanbanConnection = useCallback(
    (existing: IConnection | undefined, updated: IConnection): IConnection => {
      if (!existing) return updated;
      return {
        ...existing,
        ...updated,
        p: Array.isArray(updated.p) && updated.p.length > 0 ? updated.p : existing.p,
        firstName: findPValue("firstName", updated) || findPValue("firstName", existing),
        lastName: findPValue("lastName", updated) || findPValue("lastName", existing),
        profilePicture:
          findPValue("profilePicture", updated) ||
          findPValue("profilePicture", existing),
        headline: findPValue("headline", updated) || findPValue("headline", existing),
        publicIdentifier:
          findPValue("publicIdentifier", updated) ||
          findPValue("publicIdentifier", existing),
        profileId: findPValue("profileId", updated) || findPValue("profileId", existing),
        company: findPValue("company", updated) || findPValue("company", existing),
        location: findPValue("location", updated) || findPValue("location", existing),
      };
    },
    []
  );

  function generateId() {
    return (Math.floor(Math.random() * 1000000) + 1).toString();
  }

  const mapConnections = useCallback((connectionsData: IConnection[], kanbanId: string) => {
      return connectionsData.map((connection: IConnection) => {
          const firstName = findPValue("firstName", connection);
          const lastName = findPValue("lastName", connection);
          const profilePicture = findPValue("profilePicture", connection);
          const headline = findPValue("headline", connection);
          const publicIdentifier = findPValue("publicIdentifier", connection);
          const profileId = findPValue("profileId", connection);
          const columnId = connection?.kanbanColumns?.find(
            (c) => c.kanbanId === kanbanId
          )?.columnId;

          return {
            id: generateId(),
            columnId: columnId,
            content: `${firstName} ${lastName}`.trim(),
            connection: {
              ...connection,
              firstName,
              lastName,
              profilePicture,
              headline,
              publicIdentifier,
              tags: connection.tags,
              profileId,
            },
          };
        });
  }, []);

  const handleLoadMore = useCallback(async (columnId: Id) => {
    const colIdStr = columnId.toString();
    const currentState = columnStates[colIdStr];
    if (!currentState || currentState.loading || !currentState.hasMore) return;

    // Set loading state for this column
    setColumnStates(prev => ({
      ...prev,
      [colIdStr]: { ...currentState, loading: true }
    }));

    const currentOffset = currentState.offset ?? 0;
    try {
        const result = await getKanbanWithConnectionsHandler(
          workspace?._id,
          undefined,
          LIMIT,
          colIdStr,
          kanban?._id,
          currentOffset
        );

        if (result?.connections && result?.connections.length > 0) {
            const mappedNewConnections = mapConnections(result.connections, kanban?._id || '');
            
            setConnections(prev => {
                // Avoid duplicates based on connection._id
                const existingIds = new Set(prev.map(c => c.connection?._id));
                const filteredNew = mappedNewConnections.filter((c: any) => !existingIds.has(c.connection._id));
                return [...prev, ...filteredNew];
            });

            const newOffset = currentOffset + result.connections.length;
            setColumnStates(prev => ({
                ...prev,
                [colIdStr]: { 
                  offset: newOffset,
                  hasMore: result.connections.length >= LIMIT,
                  loading: false
                }
            }));
        } else {
            setColumnStates(prev => ({
                ...prev,
                [colIdStr]: { ...currentState, loading: false, hasMore: false }
            }));
        }
    } catch (error) {
        console.error("Error loading more connections:", error);
        setColumnStates(prev => ({
            ...prev,
            [colIdStr]: { ...currentState, loading: false }
        }));
    }
  }, [columnStates, workspace?._id, getKanbanWithConnectionsHandler, mapConnections, kanban?._id]);

  useEffect(() => {
    const loadInitialData = async () => {
      // Load all kanbans first
      if (workspace?._id) {
        const kanbansResult = await getKanbansHandler(workspace._id);
        if (kanbansResult?.success && kanbansResult?.data) {
          setAllKanbans(kanbansResult.data);
          const savedKanbanId = getStoredKanbanId(workspace._id);
          const kanbanToLoad =
            savedKanbanId &&
            kanbansResult.data.some((k: IKanban) => k._id === savedKanbanId)
              ? savedKanbanId
              : kanbansResult.data[0]?._id;
          if (kanbanToLoad) {
            await getKanbanWithConnections(kanbanToLoad);
          } else {
            setKanbanLoading(false);
          }
        } else {
          setKanbanLoading(false);
        }
      }
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace?._id]);

  const getKanbanWithConnections = useCallback(async (kanbanId?: string) => {
    setKanbanLoading(true);
    const result = await getKanbanWithConnectionsHandler(workspace?._id, undefined, undefined, undefined, kanbanId, undefined);

    if (result?.kanban) {
      setColumns(result.kanban.columns);
      setKanban(result.kanban);
    }

    let mappedConnections: Connection[] = [];
    if (result?.connections && result?.kanban?._id) {
      mappedConnections = mapConnections(result.connections, result.kanban._id) as any;
      setConnections(mappedConnections); // TODO: fix typing
    }

    if (result?.kanban) {
      // Initialize column states with correct initial offset per column
      // Prefer backend metadata.loadedPerColumn if available, otherwise fall back to FE count
      const loadedPerColumn = result?.metadata?.loadedPerColumn || {};
      const initialStates: any = {};

      result.kanban.columns.forEach((col: Column) => {
        const colIdStr = col.id.toString();
        const backendLoaded = loadedPerColumn[colIdStr];
        const frontendLoaded = mappedConnections.filter(
          (conn: Connection) => conn.columnId === col.id
        ).length;

        const initialOffset =
          typeof backendLoaded === "number" ? backendLoaded : frontendLoaded;

        initialStates[colIdStr] = {
          offset: initialOffset || 0,
          hasMore: true,
          loading: false,
        };
      });

      setColumnStates(initialStates);
    }

    setKanbanLoading(false);
  }, [mapConnections, workspace?._id, getKanbanWithConnectionsHandler]);

  // Bulk tag modal -> Kanban communication:
  // - optimistic removal on checkbox toggle
  // - refetch only if bulk tag request fails
  useEffect(() => {
    const onOptimisticRemove = (e: any) => {
      const detail = e?.detail || {};
      const eventKanbanId = detail.kanbanId as string | undefined;
      const connectionIds = (detail.connectionIds || []) as string[];
      const selectedAllConnections = detail.selectedAllConnections === true;

      if (!kanban?._id) return;
      if (!eventKanbanId || eventKanbanId !== kanban._id) return;
      if (selectedAllConnections) return;
      if (!Array.isArray(connectionIds) || connectionIds.length === 0) return;

      setConnections((prevConnections) =>
        prevConnections.filter((c) => {
          const id = c?.connection?._id;
          return !id || !connectionIds.includes(id);
        })
      );
    };

    const onRefetch = (e: any) => {
      const detail = e?.detail || {};
      const kanbanIds = (detail.kanbanIds || []) as string[];

      if (!kanban?._id) return;
      if (Array.isArray(kanbanIds) && kanbanIds.length > 0 && !kanbanIds.includes(kanban._id)) {
        return;
      }

      getKanbanWithConnections(kanban._id);
    };

    window.addEventListener("ld:kanban-optimistic-remove", onOptimisticRemove as any);
    window.addEventListener("ld:kanban-refetch", onRefetch as any);
    return () => {
      window.removeEventListener("ld:kanban-optimistic-remove", onOptimisticRemove as any);
      window.removeEventListener("ld:kanban-refetch", onRefetch as any);
    };
  }, [kanban?._id, getKanbanWithConnections]);

  async function createNewColumn() {
    let baseTitle = "Column";
    // Use the next sequential number based on the number of columns
    const nextColumnNumber = columns.length + 1;

    let columnToAdd: Column = {
      id: generateId(),
      title: `${baseTitle} ${nextColumnNumber}`,
      color: styles.colors.primary600,
    };

    // Optimistically add the column to UI
    setColumns([...columns, columnToAdd]);

    const result = await addColumnToKanbanHandler({
      kanbanId: kanban?._id,
      column: columnToAdd,
    });

    // If a new kanban was created (workspace had no kanbans)
    // sendRequest returns data.data, so result is { kanban: {...}, message: "..." }
    if (!kanban && result?.kanban) {
      const newKanban = result.kanban;
      setKanban(newKanban);
      setColumns(newKanban.columns || []);

      // Initialize column states for the new kanban
      const initialStates: any = {};
      newKanban.columns.forEach((col: Column) => {
        initialStates[col.id.toString()] = { page: 1, hasMore: true, loading: false };
      });
      setColumnStates(initialStates);

      // Refresh the kanbans list to include the new kanban
      await refreshKanbansList();

      // Load connections for the new kanban
      await getKanbanWithConnections(newKanban._id);
    } else if (kanban && result) {
      // Update the column with the actual data from the API
      // API can return either result.kanban or result.data.kanban
      const updatedKanban = result.kanban || result.data?.kanban || result.data;

      if (updatedKanban && updatedKanban.columns) {
        setKanban(updatedKanban);
        setColumns(updatedKanban.columns || []);

        // Update the kanban in the allKanbans list as well
        setAllKanbans((prevKanbans) =>
          prevKanbans.map((k) =>
            k._id === updatedKanban._id ? updatedKanban : k
          )
        );

        // Initialize column state for the new column if not already present
        const newColumnStates = { ...columnStates };
        updatedKanban.columns.forEach((col: Column) => {
          if (!newColumnStates[col.id.toString()]) {
            newColumnStates[col.id.toString()] = { offset: 0, hasMore: true, loading: false };
          }
        });
        setColumnStates(newColumnStates);
      }
    }
  }

  // removed duplicate generateId


  async function updateColumn(id: Id, title: string, color: string) {
    const editingColumn = columns.find((col) => col.id === id);
    const newColumns = columns.map((col) => {
      if (col.id !== id) return col;
      return { ...col, title, color };
    });
    setColumns(newColumns);
    const result = await updateKanbanColumnHandler(kanban?._id, {
      columnId: id,
      columnTitle: title,
      columnColor: color,
    });
    const updatedTag = result?.data?.updatedTag || result?.updatedTag;
    if (updatedTag) {
      setConnections((prevConnections: any) => {
        return prevConnections.map((conn: any) => {
          const updatedTagsArray = conn?.connection?.tags?.map(
            (tag: any) => {
              if (tag._id === updatedTag._id) {
                return updatedTag;
              }
              return tag;
            }
          );

          if (updatedTagsArray) {
            return {
              ...conn,
              connection: {
                ...conn.connection,
                tags: updatedTagsArray,
              },
            };
          }
          return conn;
        });
      });
    } else if (result?.reason === "tagExists") {
      const newColumns = columns.map((col) => {
        if (col.id !== id) return col;
        return { ...col, title: editingColumn?.title ?? "", color };
      });
      setColumns(newColumns);
    }
  }

  function updateKanban(id: Id, value: string, isName: boolean) {
    const updatedKanban = {
      ...kanban,
      [isName ? "name" : "description"]: value,
    } as IKanban;
    setKanban(updatedKanban);
    updateKanbanHandler(kanban?._id, {
      [isName ? "name" : "description"]: value,
    });
  }

  const onRemoveKanbanHandler = async (kanbanToDelete: IKanban) => {
    setDestructiveModal({
      headingText: `Are you sure you want to delete ${kanbanToDelete?.name} Pipeline?`,
      descriptionText: (
        <span>
          Deleting the <b>{kanbanToDelete?.name}</b> Pipeline will also remove all
          contacts and the associated tags. This action cannot be undone.
        </span>
      ),
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        await removeKanban(kanbanToDelete);
      },
    });
  };

  async function removeKanban(kanbanToDelete: IKanban) {
    await removeKanbanHandler(kanbanToDelete._id);
    
    // Refresh tags store to reflect removed kanban tags
    loadAllTagsHandler();
    
    // Refresh the kanbans list
    await refreshKanbansList();
    // If the deleted kanban was the currently selected one, select the first one from the new list
    if (kanban?._id === kanbanToDelete._id) {
      // Wait a bit for the list to be updated
      const updatedKanbansResult = await getKanbansHandler(workspace?._id);
      if (updatedKanbansResult?.success && updatedKanbansResult?.data && updatedKanbansResult.data.length > 0) {
        const firstKanban = updatedKanbansResult.data[0];
        // Select the first kanban and load its connections
        await onKanbanChangeHandler(firstKanban._id);
      } else {
        // No kanbans left, clear everything
        setKanban(undefined);
        setColumns([]);
        setConnections([]);
        setKanbanLoading(false);
      }
    }
  }

  async function addConnectionToColumn(columnId: Id, connection: any) {
    let newConnection: Connection = {
      id: generateId(),
      columnId,
      content: `Contact ${connections.length + 1}`,
      connection: connection,
    };
    setConnections([...connections, newConnection]);
    try {
      const result = await addConnectionToKanbanHandler(
        {
          connectionId: connection?._id,
          columnId,
          kanbanId: kanban?._id,
        },
        false
      );
      if (result?.connection) {
        const updatedConnection = {
          ...result?.connection,
          tags: result?.connection.tags,
          firstName: findPValue("firstName", result?.connection),
          lastName: findPValue("lastName", result?.connection),
          profilePicture: findPValue("profilePicture", result?.connection),
          headline: findPValue("headline", result?.connection),
          publicIdentifier: findPValue("publicIdentifier", result?.connection),
          profileId: findPValue("profileId", result?.connection),
        };
        newConnection = { ...newConnection, connection: updatedConnection };
        setConnections([...connections, newConnection]);
      } else {
        setConnections(connections.filter((c) => c.id !== newConnection.id));
        return;
      }
    } catch (error: any) {
      console.error("Error adding contact to Pipeline:");
      console.error(error);
    }
  }

  async function removeConnection(id: Id) {
    let connectionForRemove: Connection | undefined;
    try {
      // Use functional update to avoid stale state races (e.g. deleting twice quickly)
      setConnections((prev) => {
        connectionForRemove = prev.find((c) => c.id === id);
        return prev.filter((c) => c.id !== id);
      });

      const result = await removeConnectionFromKanbanHandler(
        connectionForRemove?.connection?._id,
        {
          connectionId: connectionForRemove?.connection?._id,
          kanbanId: kanban?._id,
          columnId: connectionForRemove?.columnId,
        }
      );

      if (!result?.connection) {
        if (connectionForRemove) {
          setConnections((prev) => {
            const exists = prev.some((c) => c.id === connectionForRemove!.id);
            return exists ? prev : [...prev, connectionForRemove!];
          });
        }
        return;
      }
    } catch (error) {
      console.error("Error removing contact from Pipeline:", error);
    }
  }

  function onDragStart(event: DragStartEvent) {
    if (event.active.data.current?.type === "Column") {
      setActiveColumn(event.active.data.current.column);
      return;
    }
    if (event.active.data.current?.type === "Connection") {
      setActiveConnection(event.active.data.current.connection);
      return;
    }
  }

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    setActiveColumn(null);
    setActiveConnection(null);

    if (!over) return;

    // Handle column reordering
    if (active.data.current?.type === "Column") {
      console.log(active.id, over.id);

      setColumns((columns) => {
        const activeColumnIndex = columns.findIndex(
          (col) => col.id === active.id
        );
        const overColumnIndex = columns.findIndex((col) => col.id === over.id);
        return arrayMove(columns, activeColumnIndex, overColumnIndex);
      });

      // call API
      handleColumnsReorder(active.id, over.id);
      return;
    }

    // Handle connection movement between columns
    if (dragOperation.newColumnId) {
      handleConnectionMoveToNewColumn();
    }

    // Reset drag operation
    setDragOperation({
      activeId: null,
      overId: null,
      activeIndex: null,
      overIndex: null,
      newColumnId: null,
    });
  }

  function onDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    if (activeId === overId) return;

    const isActiveAConnection = active.data.current?.type === "Connection";
    const isOverAConnection = over.data.current?.type === "Connection";
    const isOverAColumn = over.data.current?.type === "Column";

    if (!isActiveAConnection) return;

    const activeIndex = connections.findIndex((conn) => conn.id === activeId);
    let newColumnId: Id | null = null;

    if (isActiveAConnection && isOverAConnection) {
      const overIndex = connections.findIndex((conn) => conn.id === overId);

      if (
        connections[activeIndex].columnId !== connections[overIndex].columnId
      ) {
        newColumnId = connections[overIndex].columnId;
      } else {
        if (
          connections[activeIndex].columnId === connections[overIndex].columnId
        ) {
          // the same column, only reordering
          setConnections((connections) => {
            const activeIdx = connections.findIndex(
              (conn) => conn.id === activeId
            );
            const overIdx = connections.findIndex((conn) => conn.id === overId);
            if (activeIdx === -1 || overIdx === -1) return connections;
            const updated = arrayMove(connections, activeIdx, overIdx);
            return updated;
          });
        } else {
          const newColumnId = connections[overIndex].columnId;
          const kanbanId = kanban?._id;

          const updatedKanbanColumns = updateKanbanColumns(
            activeConnection?.connection?.kanbanColumns || [],
            kanbanId,
            newColumnId
          );

          const updatedConnection = {
            ...connections[activeIndex].connection,
            kanbanColumns: updatedKanbanColumns,
          };

          setConnections((connections) => {
            const activeIdx = connections.findIndex(
              (conn) => conn.id === activeId
            );
            if (activeIdx === -1) return connections;
            const updated = [...connections];
            updated[activeIdx] = {
              ...updated[activeIdx],
              columnId: newColumnId,
              connection: updatedConnection as IConnection,
            };
            return arrayMove(updated, activeIdx, overIndex);
          });
        }
      }
    } else if (isActiveAConnection && isOverAColumn) {
      newColumnId = overId;
    }

    if (newColumnId) {
      // Update UI immediately for smooth visual feedback
      const updatedKanbanColumns = updateKanbanColumns(
        activeConnection?.connection?.kanbanColumns || [],
        kanban?._id,
        newColumnId
      );

      setConnections((connections) => {
        const activeIdx = connections.findIndex((conn) => conn.id === activeId);
        if (activeIdx === -1) return connections;
        const updated = [...connections];
        updated[activeIdx] = {
          ...updated[activeIdx],
          columnId: newColumnId!,
          connection: {
            ...(updated[activeIdx].connection as Omit<IConnection, "map">),
            kanbanColumns: updatedKanbanColumns,
          },
        } as Connection;
        return updated;
      });

      // Store the drag operation for completion in onDragEnd
      setDragOperation((prev) => ({
        ...prev,
        activeId,
        overId,
        activeIndex: connections.findIndex((conn) => conn.id === activeId),
        overIndex: isOverAConnection
          ? connections.findIndex((conn) => conn.id === overId)
          : null,
        newColumnId,
      }));
    }
  }

  async function handleColumnsReorder(
    activeColumnId?: any,
    overColumnId?: any
  ) {
    try {
      if (!activeColumnId || !overColumnId) return;
      const payload = { activeColumnId, overColumnId };
      await reorderKanbanColumnsHandler(kanban?._id, payload);
    } catch (error) {
      console.error("Failed to reorder columns:", error);
      // Revert optimistic update on error
      setColumns((columns) => {
        const activeColumnIndex = columns.findIndex(
          (col) => col.id === activeColumnId
        );
        const overColumnIndex = columns.findIndex(
          (col) => col.id === overColumnId
        );
        return arrayMove(columns, overColumnIndex, activeColumnIndex);
      });
    }
  }

  async function handleConnectionMoveToNewColumn() {
    if (!dragOperation.activeId || !dragOperation.newColumnId) return;

    const { newColumnId, activeId } = dragOperation;
    const kanbanId = kanban?._id;

    const connectionToUpdate = connections.find((c) => c.id === activeId);
    if (!connectionToUpdate) return;

    try {
      const payload = { kanbanId, columnId: newColumnId };
      const result = await updateConnectionColumnIdInKanbanHandler(
        connectionToUpdate.connection?._id,
        payload
      );

      if (result?.connection) {
        const updatedConnection = {
          ...result.connection,
          tags: result.connection.tags,
          firstName: findPValue("firstName", result.connection),
          lastName: findPValue("lastName", result.connection),
          profilePicture: findPValue("profilePicture", result.connection),
          headline: findPValue("headline", result.connection),
          publicIdentifier: findPValue("publicIdentifier", result.connection),
          profileId: findPValue("profileId", result.connection),
        };

        setConnections((prevConnections) =>
          prevConnections.map((conn) =>
            conn.id === activeId
              ? {
                  ...conn,
                  connection: updatedConnection,
                  columnId: newColumnId,
                }
              : conn
          )
        );
      }
    } catch (error) {
      console.error("Failed to update contact:", error);
      // Revert optimistic update on error
      setConnections((prevConnections) => [...prevConnections]);
    }
  }

  function updateKanbanColumns(
    kanbanColumns: any[],
    kanbanId: any,
    columnId: any
  ) {
    const existingIndex = kanbanColumns.findIndex(
      (kc: any) => kc.kanbanId === kanbanId
    );

    if (existingIndex !== -1) {
      kanbanColumns[existingIndex].columnId = columnId;
    }
    // else {
    //   kanbanColumns.push({ kanbanId, columnId });
    // }

    return [...kanbanColumns];
  }

  const connectionClickedHandler = async (connection: Connection) => {
    const conn = connection?.connection;
    if (!conn?._id) return;

    const publicIdentifier =
      findPValue("publicIdentifier", conn) || (conn as any).publicIdentifier;

    let sidebarConnection: IConnection = conn;

    if (publicIdentifier) {
      const fresh = await getConnectionByPublicIdentifierHandler(publicIdentifier);
      if (fresh) sidebarConnection = fresh;
    } else {
      const fresh = await fetchConnectionByIdHandler(conn._id);
      if (fresh) sidebarConnection = fresh;
    }

    openSidebar(SidebarTypes.CONNECTION_ACTIONS, {
      connection: sidebarConnection,
      onChangedTagsHandler: handleChangeTagsViaSidebar,
      onChangedNotesHandler: handleChangeNotesViaSidebar,
    });
  };

  const handleChangeNotesViaSidebar = (updatedConnection: IConnection) => {
    if (updatedConnection) {
      setConnections((prevConnections: any[]) => {
        const newConnections = [...prevConnections];

        const index = newConnections.findIndex(
          (conn) => conn.connection?._id === updatedConnection._id
        );

        if (index === -1) return newConnections;

        const existingConnection = newConnections[index];
        newConnections[index] = {
          ...existingConnection,
          connection: mergeKanbanConnection(
            existingConnection.connection,
            updatedConnection
          ),
        };

        return newConnections;
      });
    }
  };

  const handleChangeTagsViaSidebar = (updatedConnection: IConnection) => {
    // update connections with tags changes
    if (updatedConnection) {
      setConnections((prevConnections: any[]) => {
        const newConnections = [...prevConnections];

        const forKanbanTag = updatedConnection.tags?.find(
          (tag) => tag.forKanban
        );
        const index = newConnections.findIndex(
          (conn) => conn.connection?._id === updatedConnection._id
        );

        if (index === -1) {
          // Connection doesn't exist in kanban yet, add it if it has a kanban tag
          if (forKanbanTag) {
            const newColumn = kanban?.columns.find(
              (col) => col.tag === forKanbanTag._id
            );
            if (newColumn) {
              const firstName = findPValue("firstName", updatedConnection);
              const lastName = findPValue("lastName", updatedConnection);
              const newConnection: Connection = {
                id: generateId(),
                columnId: newColumn.id,
                content: `${firstName} ${lastName}`.trim(),
                connection: updatedConnection,
              };
              newConnections.push(newConnection);
            }
          }
          return newConnections;
        }

        const existingConnection = newConnections[index];
        if (!forKanbanTag) {
          // Remove connection if no kanban tag
          newConnections.splice(index, 1);
          return newConnections;
        }

        const newColumn = kanban?.columns.find(
          (col) => col.tag === forKanbanTag._id
        );
        if (!newColumn) return newConnections;

        const newColumnId = newColumn.id;
        if (existingConnection.columnId !== newColumnId) {
          // Update connection with new columnId
          newConnections[index] = {
            ...existingConnection,
            connection: mergeKanbanConnection(existingConnection.connection, {
              ...existingConnection.connection,
              tags: updatedConnection?.tags,
            } as IConnection),
            columnId: newColumnId,
          };
        } else {
          // Update connection data but keep same column
          newConnections[index] = {
            ...existingConnection,
            connection: mergeKanbanConnection(
              existingConnection.connection,
              updatedConnection
            ),
          };
        }

        return newConnections;
      });
    }
  };

  const onRemoveColumnHandler = (column: Column) => {
    setDestructiveModal({
      headingText: `Are you sure you want to delete ${column?.title}?`,
      descriptionText: (
        <span>
          Deleting this column will also remove all contacts it contains and the
          associated tag named <b>{column?.title}</b>. This action cannot be
          undone.
        </span>
      ),
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        removeColumn(column);
      },
    });
  };

  function removeColumn(column: Column) {
    const filteredColumns = columns.filter((col) => col.id !== column?.id);
    setColumns(filteredColumns);
    const newConnections = connections.filter((t) => t.columnId !== column?.id);
    setConnections(newConnections);
    removeKanbanColumnHandler(kanban?._id, { columnId: column?.id });
  }

  const onChangeColumnColorHandler = (column: Column) => {
    setModal({
      type: ModalActionType.CUSTOM_COLOR_MODAL,
      props: {
        title: "Custom Pipeline Color",
        colorProp: column.color,
        onColorChangedHandler: (color: string) =>
          updateColumn(column.id, column.title, color),
      },
    });
  };

  const onKanbanChangeHandler = async (kanbanId: string) => {
      // If the same kanban is already selected, don't reload
      if (kanban?._id === kanbanId) {
          return;
      }

      setStoredKanbanId(workspace?._id, kanbanId);

      // Immediately update UI - find and set the selected kanban
      const selectedKanban = allKanbans.find(k => k._id === kanbanId);
      if (selectedKanban) {
          setKanban(selectedKanban);
      }
      // Load connections in the background
      setKanbanLoading(true);
      await getKanbanWithConnections(kanbanId);
  }

  const refreshKanbansList = async () => {
      if (workspace?._id) {
          const kanbansResult = await getKanbansHandler(workspace._id);
          if (kanbansResult?.success && kanbansResult?.data) {
              setAllKanbans(kanbansResult.data);
              // Update currently selected kanban if it exists in the updated list
              if (kanban?._id) {
                  const updatedKanban = kanbansResult.data.find((k: IKanban) => k._id === kanban._id);
                  if (updatedKanban) {
                      setKanban(updatedKanban);
                  }
              }
          }
      }
  }

  const updateKanbanInList = async (updatedKanban: IKanban) => {
      // Update the kanban in the list with the returned kanban from backend
      setAllKanbans(prevKanbans =>
          prevKanbans.map(k =>
              k._id === updatedKanban._id
                  ? updatedKanban
                  : k
          )
      );
      // Also update the currently selected kanban if it's the one being edited
      if (kanban?._id === updatedKanban._id) {
          setKanban(updatedKanban);
          // Reload connections to reflect changes (e.g., column name changes in tags)
          await getKanbanWithConnections(updatedKanban._id);
      }
  }

  return (
    <Wrapper>
      <HeaderWrapper>
        <KanbanInfoPopup
          kanban={kanban}
          allKanbans={allKanbans}
          updateKanban={updateKanban}
          removeKanban={onRemoveKanbanHandler}
          onKanbanChange={onKanbanChangeHandler}
          onKanbansListUpdate={refreshKanbansList}
          onUpdateKanbanInList={updateKanbanInList}
        />
      </HeaderWrapper>
      <Container>
        {kanbanLoading ? (
          <KanbanPreloader />
        ) : (
          <DndContext
            sensors={
              requestLoading?.["updateConnectionColumnIdInKanban"]
                ? []
                : sensors
            }
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
          >
            <ColumnsWrapper>
              <Columns>
                <SortableContext items={columnsId}>
                  {columns.map((col) => (
                    <ColumnContainer
                      key={col.id}
                      column={col}
                      updateColumn={updateColumn}
                      addConnectionToColumn={addConnectionToColumn}
                      connections={connections.filter(
                        (connection) => connection.columnId === col.id
                      )}
                      removeConnection={removeConnection}
                      connectionClicked={connectionClickedHandler}
                      removeColumn={onRemoveColumnHandler}
                      changeColumnColor={onChangeColumnColorHandler}
                      onLoadMore={() => handleLoadMore(col.id)}
                      loading={columnStates[col.id.toString()]?.loading || false}
                      hasMore={columnStates[col.id.toString()]?.hasMore || false}
                      currentKanbanId={kanban?._id}
                      pipelineKanbans={allKanbans}
                      activePipeline={kanban}
                    />
                  ))}
                </SortableContext>
              </Columns>
              <AddColumnButton onClick={createNewColumn}>
                <ColumnHeader>
                  <SvgFragment type="plus" />
                  <Text $label>Add Column</Text>
                </ColumnHeader>
              </AddColumnButton>
            </ColumnsWrapper>
            {createPortal(
              <DragOverlay>
                {activeColumn && (
                  <ColumnContainer
                    column={activeColumn}
                    updateColumn={updateColumn}
                    addConnectionToColumn={addConnectionToColumn}
                    connections={connections.filter(
                      (connection) => connection.columnId === activeColumn.id
                    )}
                    removeConnection={removeConnection}
                    connectionClicked={connectionClickedHandler}
                    removeColumn={onRemoveColumnHandler}
                    changeColumnColor={onChangeColumnColorHandler}
                    onLoadMore={() => {}}
                    loading={false}
                    hasMore={false}
                    currentKanbanId={kanban?._id}
                    pipelineKanbans={allKanbans}
                    activePipeline={kanban}
                  />
                )}
                {activeConnection && (
                  <ConnectionCard
                    connection={activeConnection}
                    removeConnection={removeConnection}
                    currentKanbanId={kanban?._id}
                    pipelineKanbans={allKanbans}
                    activePipeline={kanban}
                  />
                )}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        )}
      </Container>
    </Wrapper>
  );
};

const KanbanPreloader: FunctionComponent = () => {
  const columns = Array.from({ length: 3 });
  return (
    <KanbanPreloaderWrapper>
      {columns.map((_, index) => (
        <KanbanTableCell key={`preloader-row-${index}`}>
          <LoaderCell $width="100%" $height="40px" />
          <LoaderCell $width="100%" $height="150px" />
          <LoaderCell $width="100%" $height="150px" />
          <LoaderCell $width="100%" $height="150px" />
          <LoaderCell $width="100%" $height="150px" />
          <LoaderCell $width="100%" $height="150px" />
          <LoaderCell $width="100%" $height="150px" />
          <LoaderCell $width="100%" $height="150px" />
        </KanbanTableCell>
      ))}
    </KanbanPreloaderWrapper>
  );
};

const KanbanPreloaderWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 3px;
`;

const Wrapper = styled.div`
  position: relative;
  gap: 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const HeaderWrapper = styled.div`
  z-index: 101;
  flex-shrink: 0;
`;

const ColumnHeader = styled.div`
  height: 32px;
  display: flex;
  justify-content: left;
  align-items: center;
  padding: 4px 10px;
  gap: 4px;
  border-radius: 4px 4px 0 0;
  color: ${styles.colors.primary600};
  background-color: ${styles.colors.primary100};
  & span {
    color: ${styles.colors.primary600};
  }
  & svg {
    path {
      fill: ${styles.colors.primary600};
    }
  }
`;

const KanbanTableCell = styled(TableCell)`
  width: 325px;
  background-color: white;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:first-child {
    border-radius: 0px;
  }
  &:last-child {
    border-radius: 0px;
  }
`;

const Container = styled.div`
  margin: auto;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: left;
  overflow-x: scroll;
  overflow-y: hidden;
  flex: 1;
  min-height: 0;
  padding-bottom: 10px;
  
  /* Force scrollbar to always be visible on macOS */
  &::-webkit-scrollbar {
    -webkit-appearance: none;
    height: 16px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${styles.colors.black100};
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${styles.colors.black300};
    border-radius: 6px;
    min-width: 40px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${styles.colors.black400};
  }
  
  /* Firefox */
  scrollbar-width: auto;
  scrollbar-color: ${styles.colors.black300} ${styles.colors.black100};
`;

const ColumnsWrapper = styled.div`
  display: inline-flex;
  height: 100%;
  min-height: 0;
  gap: 16px;
  white-space: nowrap;
`;

const Columns = styled.div`
  display: flex;
  height: 100%;
  min-width: max-content;
  gap: 16px;
  white-space: nowrap;
`;

const AddColumnButton = styled.button`
  background-color: white;
  width: 325px;
  min-width: 325px;
  height: 100%;
  min-height: 0;
  flex-shrink: 0;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
`;

export default KanbanBoard;
